import { type FC, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Upload, Eye, EyeOff, Loader2, FolderOpen } from 'lucide-react';
import { useAuthStore } from '../store/auth-store.js';
import { LegalLinks } from '../components/LegalLinks.js';

export const Signup: FC = () => {
  const [name, setName] = useState('');
  const [creatorName, setCreatorName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { registerCreator, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await registerCreator(email, password, name, creatorName.trim() !== '' ? creatorName.trim() : undefined);
      navigate('/', { replace: true });
    } catch {
      // error is in store
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950">
      <div className="w-full max-w-sm space-y-6 px-4">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600">
            <Upload className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-xl font-bold text-slate-100">Sign up as a Creator</h1>
          <p className="mt-1 text-sm text-slate-500">Publish content directly to your library</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2.5">
            <FolderOpen className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" />
            <p className="text-xs leading-relaxed text-slate-500">
              Your workspace and a private <span className="text-slate-300">My Content</span> collection are
              created for you. Every upload is automatically routed into it.
            </p>
          </div>

          <div>
            <label htmlFor="name" className="mb-1.5 block text-xs font-medium text-slate-400">Your name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); clearError(); }}
              className="h-9 w-full rounded-lg border border-slate-700 bg-slate-800/50 px-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Jane Doe"
              required
            />
          </div>
          <div>
            <label htmlFor="creatorName" className="mb-1.5 block text-xs font-medium text-slate-400">
              Creator / brand name <span className="text-slate-600">(optional)</span>
            </label>
            <input
              id="creatorName"
              type="text"
              value={creatorName}
              onChange={(e) => { setCreatorName(e.target.value); clearError(); }}
              className="h-9 w-full rounded-lg border border-slate-700 bg-slate-800/50 px-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="My Brand"
            />
          </div>
          <div>
            <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-slate-400">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); clearError(); }}
              className="h-9 w-full rounded-lg border border-slate-700 bg-slate-800/50 px-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="you@example.com"
              required
            />
          </div>
          <div>
            <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-slate-400">Password</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearError(); }}
                className="h-9 w-full rounded-lg border border-slate-700 bg-slate-800/50 pl-3 pr-9 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="••••••••"
                minLength={8}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error !== null && (
            <div className="rounded-lg border border-red-900/50 bg-red-900/10 px-3 py-2 text-xs text-red-400">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="flex h-9 w-full items-center justify-center rounded-lg bg-blue-600 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <LegalLinks action="creating an account" />

        <p className="text-center text-xs text-slate-500">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-blue-400 hover:text-blue-300">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};